import { Pipe, PipeTransform, inject } from '@angular/core';

import { UnitService } from './unit.service';
import { Quantity, convertMeasure, formatMeasure, measureUnit } from './units';

/**
 * Renders a metric measurement in the user's preferred unit system with zero
 * call-site plumbing — the pipe owns the UnitService dependency:
 *
 *   {{ device.temperature | measure: 'temperature' }}           → "23 °C" / "73 °F"
 *   {{ trip.km | measure: 'distanceKm' }}                       → "5 km" / "3.1 mi"
 *   {{ device.temperature | measure: 'temperature': 'value' }}  → "23" / "73"
 *   {{ null | measure: 'temperature': 'unit' }}                 → "°C" / "°F"
 *
 * The 'value'/'unit' parts exist for split styling (big number, small unit).
 *
 * Impure by necessity: the transform reads the unit-system signal, and a pure
 * pipe would keep returning its memoized result after a switch (the signal
 * read marks the view dirty, but pure-pipe memoization skips re-invocation
 * when the inputs are unchanged). The transform is a cheap string build, so
 * running it per check is negligible.
 */
@Pipe({ name: 'measure', standalone: true, pure: false })
export class MeasurePipe implements PipeTransform {
  private readonly units = inject(UnitService);

  transform(
    value: number | null | undefined,
    quantity: Quantity,
    part: 'full' | 'value' | 'unit' = 'full',
  ): string {
    const system = this.units.system();
    switch (part) {
      case 'unit':
        return measureUnit(quantity, system);
      case 'value':
        return value == null ? '—' : String(convertMeasure(value, quantity, system));
      default:
        return formatMeasure(value, quantity, system);
    }
  }
}
